import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/api';
import { playMessageNotificationSound } from '../utils/messageNotificationSound';

const POLL_MS = 30000;

export default function useAdminChatReportNotifications({
  enabled = false,
  addToast,
  suppressAlerts = false,
}) {
  const [openCount, setOpenCount] = useState(0);
  const lastAlertedCountRef = useRef(null);
  const primedRef = useRef(false);
  const addToastRef = useRef(addToast);
  const suppressAlertsRef = useRef(suppressAlerts);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  useEffect(() => {
    suppressAlertsRef.current = suppressAlerts;
  }, [suppressAlerts]);

  const refreshOpenCount = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setOpenCount(0);
      lastAlertedCountRef.current = 0;
      return 0;
    }
    try {
      const res = await apiClient.listConversationReportsForAdmin({
        status: 'all',
        page: 1,
        limit: 1,
      });
      const next = Number(res.data?.stats?.open) || 0;
      const lastAlerted = lastAlertedCountRef.current;

      if (!primedRef.current) {
        primedRef.current = true;
        lastAlertedCountRef.current = next;
        setOpenCount(next);
        return next;
      }

      const hasNew = lastAlerted !== null && next > lastAlerted;
      const shouldNotify = !silent && hasNew && !suppressAlertsRef.current;

      if (shouldNotify) {
        const delta = next - lastAlerted;
        const label = delta === 1
          ? 'New chat report needs review'
          : `${delta} new chat reports need review`;
        addToastRef.current?.(label, 'info');
        if (!document.hidden) playMessageNotificationSound();
        lastAlertedCountRef.current = next;
      } else if (suppressAlertsRef.current && hasNew) {
        lastAlertedCountRef.current = next;
      } else if (lastAlerted !== null && next < lastAlerted) {
        lastAlertedCountRef.current = next;
      } else if (lastAlerted !== null && next === lastAlerted) {
        /* keep */
      }

      setOpenCount(next);
      return next;
    } catch (_) {
      return lastAlertedCountRef.current ?? 0;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setOpenCount(0);
      lastAlertedCountRef.current = 0;
      primedRef.current = false;
      return undefined;
    }

    refreshOpenCount({ silent: true });
    const id = window.setInterval(() => refreshOpenCount({ silent: false }), POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refreshOpenCount]);

  return { openCount, refreshOpenCount };
}
